import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { StickyNote, Search } from "lucide-react";
import { decodeEntities } from "@/lib/sanitize";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getRecentEpisodes } from "@/lib/recentEpisode";
import NoteButton from "@/components/NoteButton";
import EmptyStateShell from "./EmptyStateShell";

interface CandidateEpisode {
  id: string;
  slug: string;
  title: string;
  audio_url: string | null;
  duration_seconds: number | null;
  podcast_name?: string | null;
  artwork_url?: string | null;
}

/**
 * Empty state for the Notes tab.
 *
 * Source priority for "add a note" candidates:
 *  1. Saved episodes (3 most recent)
 *  2. Recently viewed episodes from localStorage
 *  3. Empty fallback → CTA to search
 */
export default function EmptyNotes() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState<CandidateEpisode[]>([]);

  useEffect(() => {
    if (!user?.id) {
      const recents = getRecentEpisodes(3);
      // Hydrate ids from slug
      if (!recents.length) return;
      supabase
        .from("episodes")
        .select("id, slug, title, audio_url, duration_seconds, podcast:podcasts(name, artwork_url)")
        .in("slug", recents.map((r) => r.slug))
        .then(({ data }) => {
          if (!data) return;
          setCandidates(
            (data as any[]).map((e) => ({
              id: e.id,
              slug: e.slug,
              title: e.title,
              audio_url: e.audio_url,
              duration_seconds: e.duration_seconds,
              podcast_name: e.podcast?.name,
              artwork_url: e.podcast?.artwork_url,
            }))
          );
        });
      return;
    }

    // Try saved episodes first
    supabase
      .from("saved_episodes")
      .select("episode:episodes(id, slug, title, audio_url, duration_seconds, podcast:podcasts(name, artwork_url))")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(3)
      .then(({ data }) => {
        const saved = (data ?? [])
          .map((row: any) => row.episode)
          .filter(Boolean)
          .map((e: any) => ({
            id: e.id,
            slug: e.slug,
            title: e.title,
            audio_url: e.audio_url,
            duration_seconds: e.duration_seconds,
            podcast_name: e.podcast?.name,
            artwork_url: e.podcast?.artwork_url,
          }));

        if (saved.length > 0) {
          setCandidates(saved);
          return;
        }

        // Fall back to recently viewed
        const recents = getRecentEpisodes(3);
        if (!recents.length) return;
        supabase
          .from("episodes")
          .select("id, slug, title, audio_url, duration_seconds, podcast:podcasts(name, artwork_url)")
          .in("slug", recents.map((r) => r.slug))
          .then(({ data: epData }) => {
            if (!epData) return;
            setCandidates(
              (epData as any[]).map((e) => ({
                id: e.id,
                slug: e.slug,
                title: e.title,
                audio_url: e.audio_url,
                duration_seconds: e.duration_seconds,
                podcast_name: e.podcast?.name,
                artwork_url: e.podcast?.artwork_url,
              }))
            );
          });
      });
  }, [user?.id]);

  return (
    <EmptyStateShell
      icon={StickyNote}
      emoji="📝"
      headline="Your second brain — empty."
      blurb="Jot timestamped notes while you listen. Searchable across every episode. Never lose a killer quote, framework, or benchmark again."
      rotation={-1.6}
    >
      {candidates.length > 0 ? (
        <div>
          <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
            ✏️ Add your first note to one of these
          </p>
          <ul className="space-y-2">
            {candidates.map((ep) => (
              <li
                key={ep.id}
                className="flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 retro-border bg-background"
              >
                {ep.artwork_url ? (
                  <img
                    src={ep.artwork_url}
                    alt=""
                    className="w-9 h-9 sm:w-10 sm:h-10 retro-border object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-9 h-9 sm:w-10 sm:h-10 retro-border bg-muted flex items-center justify-center flex-shrink-0">
                    <span className="font-retro text-xs">?</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/episode/${ep.slug}`}
                    className="block font-retro font-bold text-[11px] sm:text-sm leading-tight line-clamp-2 hover:text-primary transition-colors"
                  >
                    {decodeEntities(ep.title)}
                  </Link>
                  <p className="font-mono text-[9px] sm:text-[10px] text-muted-foreground truncate">
                    {ep.podcast_name || "Unknown podcast"}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <NoteButton
                    episodeId={ep.id}
                    episodeTitle={ep.title}
                    audioUrl={ep.audio_url}
                    durationSeconds={ep.duration_seconds}
                    podcastName={ep.podcast_name}
                    artworkUrl={ep.artwork_url}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div>
          <p className="font-mono text-[11px] sm:text-xs text-muted-foreground mb-3 leading-relaxed">
            Save an episode first, then come back to add timestamped notes from
            any saved card or playlist item.
          </p>
          <Link
            to="/"
            className="btn-comic btn-comic-shift inline-flex items-center gap-2 text-xs sm:text-sm px-4 py-2.5"
          >
            <Search className="w-4 h-4 relative z-10" />
            <span className="relative z-10">Find an episode to note</span>
          </Link>
        </div>
      )}
    </EmptyStateShell>
  );
}
